import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import {
  Users, MapPin, Calendar, DollarSign, Star, Shield,
  Search, BarChart3
} from "lucide-react";
import { format } from "date-fns";

type AdminTab = "overview" | "users" | "venues" | "bookings" | "roles";

interface AdminBooking {
  id: string;
  krew_id: string;
  venue_id: string;
  booking_date: string;
  booking_time: string;
  party_size: number;
  total_amount: number;
  status: string;
  confirmation_code: string | null;
  notes: string | null;
  table_number: string | null;
  created_at: string;
  updated_at: string;
  venue_name: string | null;
  krew_title: string | null;
}

const AdminDashboard = () => {
  const { isAdmin } = useAuth();
  const { toast } = useToast();
  const [tab, setTab] = useState<AdminTab>("overview");
  const [stats, setStats] = useState({ users: 0, venues: 0, krews: 0, bookings: 0, revenue: 0 });
  const [users, setUsers] = useState<any[]>([]);
  const [venues, setVenues] = useState<any[]>([]);
  const [bookings, setBookings] = useState<AdminBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleUserId, setRoleUserId] = useState("");
  const [roleToAssign, setRoleToAssign] = useState<string>("vendor");

  useEffect(() => {
    if (!isAdmin) return;
    const fetchAll = async () => {
      // Use server-side admin RPCs instead of direct table queries
      const [usersRes, venuesRes, krewsRes, bookingsRes] = await Promise.all([
        supabase.rpc('admin_get_all_profiles'),
        supabase.rpc('admin_get_all_venues'),
        supabase.from("krews").select("*", { count: "exact", head: true }),
        supabase.rpc('admin_get_all_bookings'),
      ]);
      if (usersRes.data) setUsers(usersRes.data);
      if (venuesRes.data) setVenues(venuesRes.data);
      const bookingsData = (bookingsRes.data || []) as AdminBooking[];
      setBookings(bookingsData);
      
      const revenue = bookingsData.filter((b) => b.status === "confirmed").reduce((s, b) => s + (b.total_amount || 0), 0);
      setStats({
        users: usersRes.data?.length || 0,
        venues: venuesRes.data?.length || 0,
        krews: krewsRes.count || 0,
        bookings: bookingsData.length,
        revenue,
      });
      setLoading(false);
    };
    fetchAll();
  }, [isAdmin]);

  const toggleVenueActive = async (venueId: string, current: boolean) => {
    const { error } = await supabase.rpc('admin_toggle_venue', { p_venue_id: venueId, p_active: !current });
    if (!error) {
      setVenues(prev => prev.map(v => v.id === venueId ? { ...v, is_active: !current } : v));
      toast({ title: current ? "Venue deactivated" : "Venue activated" });
    }
  };

  const assignRole = async () => {
    if (!roleUserId) return;
    const targetUser = users.find(u => u.user_id === roleUserId || u.display_name?.toLowerCase() === roleUserId.toLowerCase());
    if (!targetUser) {
      toast({ title: "User not found", variant: "destructive" });
      return;
    }
    const { error } = await supabase.rpc('admin_assign_role', {
      p_user_id: targetUser.user_id,
      p_role: roleToAssign as "admin" | "moderator" | "vendor" | "user",
    });
    if (error) {
      toast({ title: "Error assigning role", variant: "destructive" });
      return;
    }
    toast({ title: `${roleToAssign} role assigned to ${targetUser.display_name}` });
    setRoleUserId("");
  };

  if (!isAdmin) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <Shield className="mx-auto mb-4 h-16 w-16 text-destructive/50" />
          <h1 className="mb-2 text-2xl font-bold text-foreground">Access Denied</h1>
          <p className="text-muted-foreground">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    </div>
  );

  const filteredUsers = users.filter(u => 
    u.display_name?.toLowerCase().includes(search.toLowerCase()) || 
    u.city?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredVenues = venues.filter(v =>
    v.name?.toLowerCase().includes(search.toLowerCase()) ||
    v.city?.toLowerCase().includes(search.toLowerCase()) ||
    v.area?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">
          Admin <span className="text-gradient-neon">Dashboard</span>
        </h1>
        <p className="mt-1 text-muted-foreground">Platform management & analytics</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {[
          { icon: Users, value: stats.users, label: "Users", color: "text-primary", bg: "bg-primary/10" },
          { icon: MapPin, value: stats.venues, label: "Venues", color: "text-secondary", bg: "bg-secondary/10" },
          { icon: BarChart3, value: stats.krews, label: "Krews", color: "text-blue-400", bg: "bg-blue-500/10" },
          { icon: Calendar, value: stats.bookings, label: "Bookings", color: "text-green-400", bg: "bg-green-500/10" },
          { icon: DollarSign, value: `₹${(stats.revenue / 1000).toFixed(0)}K`, label: "Revenue", color: "text-secondary", bg: "bg-secondary/10" },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border border-border bg-card p-4">
            <div className={`mb-2 flex h-8 w-8 items-center justify-center rounded-lg ${s.bg}`}>
              <s.icon className={`h-4 w-4 ${s.color}`} />
            </div>
            <p className="text-lg font-bold text-foreground">{s.value}</p>
            <p className="text-xs text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto rounded-xl border border-border bg-card p-1">
        {(["overview", "users", "venues", "bookings", "roles"] as const).map(t => (
          <button key={t} onClick={() => { setTab(t); setSearch(""); }}
            className={`flex-1 whitespace-nowrap rounded-lg py-2.5 text-sm font-medium capitalize transition-all ${tab === t ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            {t}
          </button>
        ))}
      </div>

      {/* Search */}
      {(tab === "users" || tab === "venues") && (
        <div className="relative">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${tab}...`}
            className="w-full rounded-xl border border-border bg-card pl-11 pr-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary" />
        </div>
      )}

      {/* Tab Content */}
      <motion.div key={tab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        {tab === "overview" && (
          <div className="space-y-6">
            <div>
              <h3 className="mb-3 text-lg font-bold text-foreground">Recent Bookings</h3>
              <div className="space-y-2">
                {bookings.slice(0, 5).map(b => (
                  <div key={b.id} className="flex items-center justify-between rounded-xl border border-border bg-card p-4">
                    <div>
                      <p className="text-sm font-semibold text-foreground">{b.krew_title || "Direct"} → {b.venue_name}</p>
                      <p className="text-xs text-muted-foreground">{format(new Date(b.booking_date), "MMM d")} · {b.party_size} guests · ₹{b.total_amount?.toLocaleString()}</p>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-xs font-medium ${b.status === "confirmed" ? "bg-green-500/20 text-green-400" : b.status === "cancelled" ? "bg-destructive/20 text-destructive" : "bg-secondary/20 text-secondary"}`}>
                      {b.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="mb-3 text-lg font-bold text-foreground">New Users (Last 10)</h3>
              <div className="space-y-2">
                {users.slice(0, 10).map(u => (
                  <div key={u.id} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/20 text-xs font-bold text-primary">
                      {u.display_name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">{u.display_name}</p>
                      <p className="text-xs text-muted-foreground">{u.city || "No city"} · Score: {u.reliability_score}%</p>
                    </div>
                    <span className="text-xs text-muted-foreground">{format(new Date(u.created_at), "MMM d")}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "users" && (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">{filteredUsers.length} users</p>
            {filteredUsers.map(u => (
              <div key={u.id} className="flex items-center gap-3 rounded-xl border border-border bg-card p-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/20 text-sm font-bold text-primary">
                  {u.display_name?.charAt(0)?.toUpperCase() || "?"}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">{u.display_name}</p>
                  <p className="text-xs text-muted-foreground">{u.city || "—"} · Joined {u.total_krews_joined} krews · Score: {u.reliability_score}%</p>
                </div>
                <div className="text-right">
                  {u.is_premium && <span className="rounded-full bg-secondary/20 px-2 py-0.5 text-xs font-medium text-secondary">Premium</span>}
                  <p className="text-xs text-muted-foreground mt-1">{format(new Date(u.created_at), "MMM d, yyyy")}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "venues" && (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">{filteredVenues.length} venues</p>
            {filteredVenues.map(v => (
              <div key={v.id} className="flex items-center gap-4 rounded-xl border border-border bg-card p-4">
                {v.image_url ? (
                  <img src={v.image_url} alt={v.name} className="h-12 w-12 rounded-lg object-cover" />
                ) : (
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted"><MapPin className="h-5 w-5 text-muted-foreground" /></div>
                )}
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">{v.name}</p>
                  <p className="text-xs text-muted-foreground">{v.area}, {v.city} · {v.category} · ₹{v.avg_price_per_person}/person</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 text-xs">
                    <Star className="h-3 w-3 text-secondary" />
                    <span className="text-foreground">{v.rating}</span>
                  </div>
                  <button onClick={() => toggleVenueActive(v.id, v.is_active)}
                    className={`rounded-full px-3 py-1 text-xs font-medium ${v.is_active ? "bg-green-500/20 text-green-400" : "bg-destructive/20 text-destructive"}`}>
                    {v.is_active ? "Active" : "Hidden"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "bookings" && (
          <div className="space-y-2">
            {bookings.map(b => (
              <div key={b.id} className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{b.krew_title || "Direct"} → {b.venue_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(b.booking_date), "EEE, MMM d")} at {b.booking_time?.slice(0, 5)} · {b.party_size} guests · ₹{b.total_amount?.toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground">Code: <span className="font-mono text-primary">{b.confirmation_code}</span></p>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-xs font-medium ${b.status === "confirmed" ? "bg-green-500/20 text-green-400" : b.status === "cancelled" ? "bg-destructive/20 text-destructive" : "bg-secondary/20 text-secondary"}`}>
                    {b.status}
                  </span>
                </div>
              </div>
            ))}
            {bookings.length === 0 && <p className="py-8 text-center text-muted-foreground">No bookings yet.</p>}
          </div>
        )}

        {tab === "roles" && (
          <div className="space-y-6">
            <div className="rounded-xl border border-border bg-card p-6">
              <h3 className="mb-4 text-lg font-bold text-foreground">Assign Role</h3>
              <div className="flex flex-col gap-3 sm:flex-row">
                <input value={roleUserId} onChange={(e) => setRoleUserId(e.target.value)}
                  placeholder="User display name or ID"
                  className="flex-1 rounded-xl border border-border bg-muted px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none" />
                <select value={roleToAssign} onChange={(e) => setRoleToAssign(e.target.value)}
                  className="rounded-xl border border-border bg-muted px-4 py-3 text-sm text-foreground focus:border-primary focus:outline-none">
                  <option value="vendor">Vendor</option>
                  <option value="admin">Admin</option>
                  <option value="moderator">Moderator</option>
                </select>
                <button onClick={assignRole}
                  className="rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground">
                  Assign
                </button>
              </div>
            </div>
            <div>
              <h3 className="mb-3 text-lg font-bold text-foreground">Quick Assign</h3>
              <p className="mb-4 text-sm text-muted-foreground">Click a user below to assign them a role.</p>
              <div className="space-y-2">
                {users.slice(0, 20).map(u => (
                  <div key={u.id} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/20 text-xs font-bold text-primary">
                      {u.display_name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">{u.display_name}</p>
                      <p className="text-xs text-muted-foreground">{u.city || "—"}</p>
                    </div>
                    <button onClick={() => setRoleUserId(u.display_name)}
                      className="rounded-lg border border-border px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:border-primary transition-all">
                      Select
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AdminDashboard;
